import type {
  BlockStateUnionType,
  BlockType,
  ComponentTypeUnionType,
} from '@unifyapps/defs/types/block';
import type { ReactNode } from 'react';
import type { EntityDependencyNode } from '../../../dependency/manager/types';
import { BlockHelper } from '../../../helpers/BlockHelper';
import { useCanAccess } from '../../access-control/hooks/useCanAccess';
import { useSlot } from '../../components/page/InterfacePageProviders/context/SlotsProvider';
import { useBlockRuntimeData, useRuntimeBlockKey } from '../../context/BlockRuntimeStateProvider';
import { useDependencyGraphProvider } from '../../context/DependencyGraphProvider';
import { useRegistryContext } from '../../context/RegistryProvider';
import type { BlockRendererWrapperProps } from '../../context/RegistryProvider/types';
import { useSlotContextSnapshots } from '../../context/SlotContextSnapshotsProvider/SlotContextSnapshotsContext';
import useSetBlockLifecycle from '../../hooks/useSetBlockLifecycle';
import useSetBlockState from '../../hooks/useSetBlockState';
import type { SlotContextSnapshot } from '../../stores/GlobalStateStore/types/slotContext';
import { useBlockFromPage } from '../../stores/InterfaceStore';
import { isEmptyBlock } from '../../utils/block';

export type RendererMode = 'runner' | 'builder';

type UseSetBlockStateReturn = ReturnType<typeof useSetBlockState>;

export type RendererCoreResult =
  | { kind: 'slot'; slot: ReactNode }
  | { kind: 'null' }
  | { kind: 'placeholder'; parentBlockId: string | null }
  | {
      kind: 'render';
      block: BlockType;
      blockState: NonNullable<UseSetBlockStateReturn['blockState']>;
      blockStateId: string;
      blocksRuntimeState: Record<string, Record<string, unknown>> | undefined;
      slotContextSnapshots: Record<string, SlotContextSnapshot> | undefined;
      updateBlockState: UseSetBlockStateReturn['updateBlockState'];
      updateBlockSessionState: UseSetBlockStateReturn['updateBlockSessionState'];
      shouldSkipErrorBoundary: boolean;
      useWithContextPipeline: boolean;
    };

// Exported for unit testing — pure decision over (block, blockState, graph node).
export function decideUseWithContextPipeline(
  block: BlockType,
  blockState: BlockStateUnionType,
  node: EntityDependencyNode | null | undefined,
): boolean {
  // INVARIANT: per `VisibilityPayloadType` in `@unifyapps/defs/types/visibilityPayload`,
  // the only non-boolean visibility shape is `{ value: 'conditions', conditions }`.
  // If a new evaluation mode is ever added there, broaden this guard in the
  // same diff or static blocks with that mode will silently skip the
  // condition-eval pipeline and render unconditionally.
  if (blockState.visibility?.value === 'conditions') return true;
  if (BlockHelper.hasDynamicPaths(block)) return true;
  // Self-referential conditionals (e.g. color/icon on own state) populate cP but not `dependsOn`.
  if (BlockHelper.hasConditionalPaths(block)) return true;
  if (!node) return true;
  return node.dependsOn.length > 0;
}

export function useBlockRendererCore(
  props: BlockRendererWrapperProps,
  options: { mode: RendererMode },
): RendererCoreResult {
  const { blockId, parentBlockId } = props;
  const { mode } = options;

  const block = useBlockFromPage({ blockId });
  const { slot } = useSlot(blockId ?? '');
  const { registry } = useRegistryContext();
  const shouldSkipErrorBoundary = registry.getShouldBlockSkipErrorBoundary(
    block?.component.componentType,
  );
  const runtimeBlockKey = useRuntimeBlockKey();
  const { data: blocksRuntimeState } = useBlockRuntimeData();
  const slotContextSnapshots = useSlotContextSnapshots();

  const { blockState, updateBlockState, updateBlockSessionState, blockStateId } = useSetBlockState({
    originalBlockId: blockId ?? undefined,
    runtimeBlockKey,
  });

  const isBlockPermissible = useCanAccess(block?.permissions);
  const { graphManager } = useDependencyGraphProvider();

  // Gate the lifecycle transition: the old withBlockState HOC returned null
  // BEFORE downstream effects ran, so denied/invisible/empty blocks never
  // announced Staging. The hook itself must still be called unconditionally
  // (rules of hooks); the flag short-circuits its effect body. Mirrors the
  // null-kind preconditions below — keep these in sync.
  const isLifecycleEnabled =
    !isEmptyBlock(blockId) &&
    Boolean(block) &&
    isBlockPermissible &&
    Boolean(blockState) &&
    blockState?.visibility?.value !== false;

  useSetBlockLifecycle(
    block?.id ?? '',
    block?.component.componentType ?? ('' as ComponentTypeUnionType),
    isLifecycleEnabled,
  );

  if (slot) return { kind: 'slot', slot };

  if (mode === 'builder' && isEmptyBlock(blockId)) {
    return { kind: 'placeholder', parentBlockId: parentBlockId ?? null };
  }

  if (isEmptyBlock(blockId) || !block || !isBlockPermissible || !blockState) {
    return { kind: 'null' };
  }

  if (blockState.visibility?.value === false) {
    return { kind: 'null' };
  }

  const node = graphManager.getNode(block.id);
  const useWithContextPipeline = decideUseWithContextPipeline(block, blockState, node);

  return {
    kind: 'render',
    block,
    blockState,
    blockStateId: blockStateId ?? '',
    blocksRuntimeState,
    slotContextSnapshots,
    updateBlockState,
    updateBlockSessionState,
    shouldSkipErrorBoundary,
    useWithContextPipeline,
  };
}
